import { Entity } from '$legacyecstatic';
import ECSMapContext from 'contexts/ECSMapContext';
import ECSWorldContext from 'contexts/ECSWorldContext';
import { Components } from 'editor/components';
import Events from 'editor/components/Events';
import MapSheetEvents from 'editor/components/map/MapboxEvents/MapSheetEvents';
import MapboxLayer from 'editor/components/map/MapboxLayer';
import MapboxRasterSource from 'editor/components/map/MapboxRasterSource';
import MapSheet from 'editor/components/map/MapSheet';
import PageID from 'editor/components/PageID';
import { FeatureFlag, useFeatureFlags } from 'featureFlags';
import React, { FC, useContext, useEffect, useState } from 'react';
import { ImageSource, MappedSheetVersion } from 'records/MappedSheetVersion';
import { getBoundingBoxOfImage, getCroppedPDFSourceUrl } from 'utils/map';
import withAppStore, { WithAppStoreProps } from 'utils/withAppStore';
import ECSMapSheetMarkups from '../ECSMap/ECSMapSheetMarkups';
import ECSMapSheetEvents from './ECSMapSheetEvents';

interface MapSheetProps extends WithAppStoreProps {
  mappedSheetVersion: MappedSheetVersion;
}

const ECSMapSheet: FC<MapSheetProps> = ({ mappedSheetVersion, appStore }) => {
  const { map: mapComp } = useContext(ECSMapContext);
  const world = useContext(ECSWorldContext);
  const { [FeatureFlag.MapTiles]: isMapTilesEnabled } = useFeatureFlags();
  const [sheetLayerEntity, setSheetLayerEntity] =
    useState<Entity<Components> | null>();

  const { imagesource } = mappedSheetVersion;

  const canvasId = `${mappedSheetVersion.imagesource?.id as string}-canvas`;
  const { state } = appStore;

  const sheetVersion = state.getSheetVersionByPageId(mappedSheetVersion.pageid);

  // NOTE: I think that clip path logic should probably be in a system,
  //       especially since it affects the rendering of a sheet.
  //       I think implementing this would be a good first step in ECS.
  const getUrl = async (): Promise<string> => {
    let url = imagesource?.url as string;

    if (
      imagesource?.calibrationconfig.croppath &&
      imagesource.calibrationconfig.croppath.length > 0
    ) {
      url = await getCroppedPDFSourceUrl(
        sheetVersion,
        mappedSheetVersion.imagesource?.calibrationconfig.croppath as Point[],
        mappedSheetVersion.imagesource?.url as string,
        canvasId
      );
    }

    return url;
  };

  type MapSourceConfig = {
    source: mapboxgl.ImageSourceRaw | mapboxgl.RasterSource;
    coordinates: number[][];
  };

  const getMapSourceConfig = async (
    imgSource: ImageSource
  ): Promise<MapSourceConfig> => {
    const { topleft, topright, bottomright, bottomleft } =
      imgSource.calibrationconfig.corners;

    const coordinates = [
      [topleft.longitude, topleft.latitude],
      [topright.longitude, topright.latitude],
      [bottomright.longitude, bottomright.latitude],
      [bottomleft.longitude, bottomleft.latitude],
    ];

    if (isMapTilesEnabled && sheetVersion.maptilecloudfronturl) {
      const {
        maptilecloudfronturl,
        minzoom,
        maxzoom,
        tilesize: tileSize,
      } = sheetVersion;

      return {
        source: {
          bounds: getBoundingBoxOfImage(coordinates),
          maxzoom,
          minzoom,
          tileSize,
          tiles: [maptilecloudfronturl],
          type: 'raster',
        },
        coordinates,
      };
    }

    const url = await getUrl();

    return {
      source: {
        type: imgSource.type,
        url,
        coordinates,
      },
      coordinates,
    };
  };

  // Create sheet layer
  useEffect(() => {
    const sheetLayerTag = `sheetLayer-${mappedSheetVersion.pageid}`;

    async function createSheetLayer(): Promise<void> {
      if (!imagesource) return;

      const { source, coordinates } = await getMapSourceConfig(imagesource);

      const _sourceComp = new MapboxRasterSource(
        source,
        sheetVersion.width,
        sheetVersion.height,
        coordinates
      );

      const _layerComp = new MapboxLayer({
        id: `${_sourceComp.id}-layer`,
        source: _sourceComp.id,
        type: 'raster',
      });

      const mapSheetEventsComp = new MapSheetEvents(world);

      // NOTE: Right now the Mapbox render event that calls is on SheetEvents.
      //       Should there be separate Events component just for Map Events?
      const sheetEventsComp = new Events(mapSheetEventsComp);

      const entity = world
        .createEntity()
        .add(mapComp)
        .add(_sourceComp)
        .add(_layerComp)
        .add(sheetEventsComp)
        .add(new PageID(mappedSheetVersion.pageid))
        .add(new MapSheet())
        .addTag(sheetLayerTag);

      setSheetLayerEntity(entity);

      // Needed for now because Sheet Events holds the Mapbox render event that calls system.run, and needs to be added.
      world.systems.run();
    }

    createSheetLayer();

    return () => {
      if (mapComp.map) {
        const entity = world.getTagged(sheetLayerTag);
        if (!entity) return;

        entity.destroy();

        // Make sure entities that have a "destroying" state are handled in systems.
        world.systems.run();
      }
    };
  }, []);

  // Update sheet layer on mappedSheetVersion change.
  useEffect(() => {
    async function updateSheetLayer() {
      if (!sheetLayerEntity || !imagesource) return;

      const { source, coordinates } = await getMapSourceConfig(imagesource);

      if (sheetLayerEntity.has(MapboxRasterSource)) {
        const _sourceComp = sheetLayerEntity.get(MapboxRasterSource);
        _sourceComp.tracked.source = source;
        _sourceComp.tracked.coordinates = coordinates;
      }
    }

    updateSheetLayer();
  }, [sheetLayerEntity, mappedSheetVersion]);

  return sheetLayerEntity ? (
    <>
      <ECSMapSheetEvents sheetLayerEntity={sheetLayerEntity} />
      <ECSMapSheetMarkups mappedSheetVersion={mappedSheetVersion} />
    </>
  ) : null;
};

export default withAppStore<MapSheetProps>(ECSMapSheet);
